/**
 * algolia-insights.js -- Algolia Insights click + conversion events (#341).
 *
 * Why this exists: the Algolia dashboard counted searches but showed no
 * click-through or conversion data. The header search overlay and `/search`
 * are custom-built on the Algolia JS client rather than the Algolia app's
 * widgets, so nothing asked Algolia for a `queryID` (`clickAnalytics`) or sent
 * events back to the Insights API. The headless storefront never sent them
 * either, so this is new instrumentation rather than a restored one.
 *
 * What it does:
 *   1. Search params -- `searchParams(indexName)` returns
 *      `{ clickAnalytics: true, userToken }` for the products index and its
 *      sort replicas. `RTLAlgolia.createSearchClient()` merges it into every
 *      search request, and `RTLAlgolia.getRecommendations()` into every
 *      Recommend request, so each result set comes back with a `queryID`.
 *   2. Clicks -- `clickedAfterSearch()` sends "Product Clicked After Search"
 *      when a shopper opens a product from the overlay or `/search`, and
 *      `clickedRecommendation()` sends "Product Clicked After Recommendation"
 *      when they open one from a PDP recommendation surface (cards tagged and
 *      bound in `algolia-recommendations.js`). Both remember the search or
 *      recommendation, and which of the two it was, against the product ID.
 *      `attributeRecommendation()` remembers without a click event, for a
 *      quick-add straight from a recommended card.
 *   3. Add to cart -- `trackAddedToCart(addResponse, quantity)`, called from
 *      every add-to-cart success path (the same call sites as `RTLKlaviyo`),
 *      sends "Product Added To Cart After Search" (or "... After
 *      Recommendation") for any added product that was opened from search or
 *      a recommendation, then records the variant + query on the cart.
 *   4. Purchase -- the theme does not run on checkout, so the purchase event
 *      is sent by a Customer Events custom pixel
 *      (`docs/08-documentation/algolia-search-purchases-pixel.js`) that reads
 *      the `_algolia_insights` cart attribute written in step 3. A cart
 *      attribute rather than localStorage because the storefront and checkout
 *      may not share an origin (shoppers browse runawaythelabel.com/au while
 *      the AU store's primary domain is au.runawaythelabel.com), and the AU
 *      and US storefronts can share one.
 *
 * Consent: nothing runs unless
 * `Shopify.customerPrivacy.analyticsProcessingAllowed()` is true -- no
 * `clickAnalytics`, no `_ALGOLIA` cookie, no events. When the shopper
 * withdraws consent the cookie and the remembered searches are deleted.
 *
 * Why not the search-insights library: the Algolia app embed (autocomplete
 * block, enabled in the theme editor) already claims the global `window.aa`
 * and re-inits it with its own options, so a second copy would fight it.
 * Events go straight to the Insights REST API the way search-insights sends
 * them (sendBeacon, credentials in the query string), and the user token uses
 * the library's `_ALGOLIA` cookie format so the two stay interchangeable.
 *
 * No framework. Vanilla JS, no dependencies, defer-loaded from `theme.liquid`
 * after `algolia-client.js`.
 */
(function () {
  'use strict';

  var INSIGHTS_URL = 'https://insights.algolia.io/1/events';
  var COOKIE_KEY = '_ALGOLIA';
  // 180 days -- the search-insights default for the same cookie.
  var COOKIE_MAX_AGE_SECONDS = 15552000;
  var USER_TOKEN_PATTERN = /^[a-zA-Z0-9_=\/+-]{1,129}$/;
  var CLICKS_KEY = 'rtl_algolia_insights_clicks';
  var CART_ATTRIBUTE = '_algolia_insights';
  // A search click only counts towards an add to cart inside this window.
  var CLICK_TTL_MS = 24 * 60 * 60 * 1000;
  var MAX_CLICKS = 50;
  // How long `whenReady()` waits for Shopify's consent API to load.
  var CONSENT_WAIT_MS = 1000;
  // Event names per source. The `queryID` is what attributes an event to
  // Search or Recommend in the dashboard; the names keep the two apart in the
  // Events debugger.
  const EVENT_NAMES = {
    search: {
      click: 'Product Clicked After Search',
      addToCart: 'Product Added To Cart After Search',
    },
    recommend: {
      click: 'Product Clicked After Recommendation',
      addToCart: 'Product Added To Cart After Recommendation',
    },
  };

  // Clicks remembered before recommendations were tracked carry no source,
  // and they all came from search. Own-property only: the clicks come back
  // from shopper-writable localStorage, and a bare lookup would also accept
  // "toString" off the prototype chain and send an event with no name.
  function sourceOf(click) {
    const source = click && click.source;
    return Object.prototype.hasOwnProperty.call(EVENT_NAMES, source)
      ? source
      : 'search';
  }

  function config() {
    var algolia = window.RTLAlgolia;
    return algolia && typeof algolia.getConfig === 'function'
      ? algolia.getConfig()
      : null;
  }

  // The products index and its sort replicas -- the indices whose result
  // clicks we send. Collections and query suggestions are not tracked, so they
  // must not get `clickAnalytics` either (tracked searches with no possible
  // click would drag their click-through rate down).
  function isTrackedIndex(indexName) {
    var cfg = config();
    if (!cfg || !indexName) return false;
    if (indexName === cfg.productsIndex) return true;
    var replicas = cfg.replicas || {};
    return Object.keys(replicas).some(function (key) {
      return replicas[key] === indexName;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Consent                                                            */
  /* ------------------------------------------------------------------ */

  function consentApi() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    return cp && typeof cp.analyticsProcessingAllowed === 'function' ? cp : null;
  }

  // Shopify returns true where the visitor's region needs no consent, so this
  // only blocks tracking where a banner is required and has not been
  // accepted. A missing API (blocked script) counts as "not allowed".
  function analyticsAllowed() {
    try {
      var cp = consentApi();
      return cp ? cp.analyticsProcessingAllowed() === true : false;
    } catch (err) {
      return false;
    }
  }

  // Resolves once the consent API has loaded, or after CONSENT_WAIT_MS. Never
  // rejects. `/search` waits on this before its first query so that query is
  // tracked when analytics is allowed.
  var readyPromise = null;
  function whenReady() {
    if (!readyPromise) {
      readyPromise = new Promise(function (resolve) {
        var started = Date.now();
        (function check() {
          if (consentApi() || Date.now() - started >= CONSENT_WAIT_MS) {
            resolve();
            return;
          }
          setTimeout(check, 50);
        })();
      });
    }
    return readyPromise;
  }

  /* ------------------------------------------------------------------ */
  /* User token                                                         */
  /* ------------------------------------------------------------------ */

  function readCookie(name) {
    var prefix = name + '=';
    var parts = document.cookie ? document.cookie.split(';') : [];
    for (var i = 0; i < parts.length; i += 1) {
      var part = parts[i].replace(/^\s+/, '');
      if (part.indexOf(prefix) === 0) return part.slice(prefix.length);
    }
    return '';
  }

  function writeCookie(name, value, maxAgeSeconds) {
    document.cookie =
      name + '=' + value + ';max-age=' + maxAgeSeconds + ';path=/;SameSite=Lax';
  }

  function createUuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 3) | 8).toString(16);
    });
  }

  // The same anonymous token + cookie search-insights would create. Only
  // called once analytics consent has been checked.
  function userToken() {
    var token = readCookie(COOKIE_KEY);
    if (!USER_TOKEN_PATTERN.test(token)) {
      token = 'anonymous-' + createUuid();
      writeCookie(COOKIE_KEY, token, COOKIE_MAX_AGE_SECONDS);
    }
    return token;
  }

  /* ------------------------------------------------------------------ */
  /* Remembered search clicks (product ID -> search)                    */
  /* ------------------------------------------------------------------ */

  function readClicks() {
    try {
      var parsed = JSON.parse(window.localStorage.getItem(CLICKS_KEY) || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (err) {
      return {};
    }
  }

  // Drop expired clicks and keep the newest MAX_CLICKS.
  function writeClicks(clicks) {
    var now = Date.now();
    var kept = {};
    Object.keys(clicks)
      .filter(function (id) {
        return clicks[id] && clicks[id].expires > now;
      })
      .sort(function (a, b) {
        return clicks[b].expires - clicks[a].expires;
      })
      .slice(0, MAX_CLICKS)
      .forEach(function (id) {
        kept[id] = clicks[id];
      });
    try {
      window.localStorage.setItem(CLICKS_KEY, JSON.stringify(kept));
    } catch (err) {
      /* Storage full or blocked -- the click event itself was still sent. */
    }
  }

  function forget() {
    try {
      window.localStorage.removeItem(CLICKS_KEY);
    } catch (err) {
      /* Storage blocked -- nothing stored to remove. */
    }
    if (readCookie(COOKIE_KEY)) writeCookie(COOKIE_KEY, '', 0);
  }

  /* ------------------------------------------------------------------ */
  /* Sending                                                            */
  /* ------------------------------------------------------------------ */

  // Credentials go in the query string so the request can use sendBeacon,
  // which survives the page navigating away -- a product click always does.
  // The fetch fallback sends a plain-text body for the same reason
  // search-insights does: it keeps the request CORS-simple (no preflight).
  function send(events) {
    var cfg = config();
    if (!cfg || !cfg.applicationId || !cfg.searchApiKey || !events.length) {
      return;
    }
    var url =
      INSIGHTS_URL +
      '?X-Algolia-Application-Id=' +
      encodeURIComponent(cfg.applicationId) +
      '&X-Algolia-API-Key=' +
      encodeURIComponent(cfg.searchApiKey);
    var body = JSON.stringify({ events: events });
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url, body)) return;
    } catch (err) {
      /* Fall through to fetch. */
    }
    try {
      fetch(url, { method: 'POST', body: body, keepalive: true }).catch(
        function () {}
      );
    } catch (err) {
      /* Analytics must never break the page. */
    }
  }

  /* ------------------------------------------------------------------ */
  /* Public API                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Extra search parameters for a request against `indexName`, or null when
   * the index is not tracked or analytics is not allowed.
   */
  function searchParams(indexName) {
    if (!isTrackedIndex(indexName) || !analyticsAllowed()) return null;
    return { clickAnalytics: true, userToken: userToken() };
  }

  // Remember the search or recommendation a product was found from, so a
  // later add to cart of the same product can be attributed to it. The latest
  // one wins when a product is found both ways.
  function remember(click, source) {
    if (!click.productId) return;
    const clicks = readClicks();
    clicks[String(click.productId)] = {
      index: click.index,
      queryID: click.queryID,
      source: source,
      expires: Date.now() + CLICK_TTL_MS,
    };
    writeClicks(clicks);
  }

  function trackClick(click, source) {
    if (!click || !click.index || !click.queryID || !click.objectID) return;
    var position = parseInt(click.position, 10);
    if (!(position > 0)) return;
    if (!analyticsAllowed()) return;

    send([
      {
        eventType: 'click',
        eventName: EVENT_NAMES[source].click,
        index: click.index,
        queryID: click.queryID,
        objectIDs: [String(click.objectID)],
        positions: [position],
        userToken: userToken(),
        timestamp: Date.now(),
      },
    ]);
    remember(click, source);
  }

  /**
   * Send a click on a product search result and remember the search so a
   * later add to cart of the same product can be attributed to it.
   *
   * @param {{index: string, queryID: string, objectID: string, productId: (string|number), position: number}} click
   *   `objectID` is the Algolia record (a variant ID -- the index holds one
   *   record per variant), `productId` the Shopify product ID, `position` the
   *   1-based absolute position in the results.
   */
  function clickedAfterSearch(click) {
    trackClick(click, 'search');
  }

  /**
   * Send a click on a recommended product and remember the recommendation,
   * as `clickedAfterSearch` does for search. Same `click` shape; `position`
   * is the 1-based rank in the Recommend response.
   */
  function clickedRecommendation(click) {
    trackClick(click, 'recommend');
  }

  /**
   * Remember a recommendation for the add to cart that follows, without a
   * click event: quick-add, where the shopper picks a size on the card
   * without opening the product. Algolia counts that add as a conversion on
   * the recommendation, but it is not a click-through.
   *
   * @param {{index: string, queryID: string, productId: (string|number)}} click
   */
  function attributeRecommendation(click) {
    if (!click || !click.index || !click.queryID) return;
    if (!analyticsAllowed()) return;
    remember(click, 'recommend');
  }

  // Normalise every /cart/add.js response shape into an array of line items:
  // a single line item (form-encoded POST), `{ items: [...] }` (JSON POST),
  // or an array.
  function toItems(response) {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (Array.isArray(response.items)) return response.items;
    if (response.id || response.variant_id || response.product_id) {
      return [response];
    }
    return [];
  }

  // Shopify prices are integer cents; Algolia expects a decimal amount.
  function money(cents) {
    var n = Number(cents);
    if (isNaN(n)) return 0;
    return Math.round(n) / 100;
  }

  function round2(value) {
    return Math.round(value * 100) / 100;
  }

  // Each write waits for the one before it. A write reads the attribute, merges
  // and posts it back, so two overlapping writes (two quick adds after search
  // clicks) would both read the old value and the second would drop the
  // first's variant. The chain never rejects: every write ends in a catch.
  var cartWrite = Promise.resolve();

  // Record `{ variantId: { index, queryID, source } }` on the cart for the
  // purchase pixel, merged with what the cart already carries and pruned to
  // variants still in the cart.
  function rememberOnCart(token, attributed) {
    cartWrite = cartWrite.then(function () {
      return writeAttribution(token, attributed);
    });
  }

  function writeAttribution(token, attributed) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        if (!cart) return null;
        var existing = {};
        try {
          var raw = cart.attributes ? cart.attributes[CART_ATTRIBUTE] : '';
          existing = (raw && JSON.parse(raw).items) || {};
        } catch (err) {
          existing = {};
        }
        var merged = Object.assign({}, existing, attributed);
        var items = {};
        (cart.items || []).forEach(function (line) {
          var id = String(line.variant_id || line.id);
          if (merged[id]) items[id] = merged[id];
        });
        var attributes = {};
        attributes[CART_ATTRIBUTE] = JSON.stringify({
          userToken: token,
          items: items,
        });
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ attributes: attributes }),
        });
      })
      .catch(function () {
        /* The add-to-cart event was still sent; only purchase attribution is lost. */
      });
  }

  /**
   * Send "Product Added To Cart After Search" (or "... After Recommendation")
   * for every added line whose product was opened from search or a
   * recommendation in the last CLICK_TTL_MS. No-ops for products that came
   * from neither.
   *
   * @param {Object|Array} addResponse - the raw `/cart/add.js` response.
   * @param {(number|string)} [addedQuantity] - units this add requested per
   *   line, default 1. Not read from the response: its `quantity` is the
   *   line's new total in the cart, so adding a size already in the bag
   *   would report the whole line rather than the one unit added.
   */
  function trackAddedToCart(addResponse, addedQuantity) {
    if (!analyticsAllowed()) return;
    var added = toItems(addResponse);
    if (!added.length) return;
    var quantity = parseInt(addedQuantity, 10) || 1;

    var clicks = readClicks();
    var now = Date.now();
    var cfg = config();
    var token = null;
    var events = [];
    var attributed = {};

    added.forEach(function (item) {
      var click = clicks[String(item.product_id)];
      var variantId = item.variant_id || item.id;
      if (!click || !(click.expires > now) || !variantId) return;

      const source = sourceOf(click);
      if (!token) token = userToken();
      var price = money(item.price);
      var finalPrice = money(
        item.final_price !== undefined && item.final_price !== null
          ? item.final_price
          : item.price
      );
      var objectData = {
        queryID: click.queryID,
        price: finalPrice,
        quantity: quantity,
      };
      if (price > finalPrice) objectData.discount = round2(price - finalPrice);

      // Variant ID, not the clicked record: the shopper may add a different
      // size, and every variant is its own record in the index.
      events.push({
        eventType: 'conversion',
        eventSubtype: 'addToCart',
        eventName: EVENT_NAMES[source].addToCart,
        index: click.index,
        queryID: click.queryID,
        objectIDs: [String(variantId)],
        objectData: [objectData],
        value: round2(finalPrice * quantity),
        currency: cfg ? cfg.currencyCode : 'AUD',
        userToken: token,
        timestamp: now,
      });
      attributed[String(variantId)] = {
        index: click.index,
        queryID: click.queryID,
        source: source,
      };
    });

    if (!events.length) return;
    send(events);
    rememberOnCart(token, attributed);
  }

  // Withdrawn consent: delete the token cookie and remembered searches. Only
  // when the API is present and says no -- a slow-loading API must not wipe a
  // consenting shopper's token.
  whenReady().then(function () {
    if (consentApi() && !analyticsAllowed()) forget();
  });
  document.addEventListener('visitorConsentCollected', function () {
    if (!analyticsAllowed()) forget();
  });

  window.RTLAlgoliaInsights = {
    searchParams: searchParams,
    whenReady: whenReady,
    clickedAfterSearch: clickedAfterSearch,
    clickedRecommendation: clickedRecommendation,
    attributeRecommendation: attributeRecommendation,
    trackAddedToCart: trackAddedToCart,
  };
})();
